// AIRLINEOPS - REPUTATION ENGINE
// Rolling 30-day weighted average with decay, Tiers 1-5, EOS tracking

// TIER THRESHOLDS - Per CLAUDE_PROTOCOL.md Section 5.2
// These are CANONICAL - do not modify without updating the protocol
export const TIER_THRESHOLDS = {
  1: { min: 0, max: 2999, name: "Startup", color: "#737373" },
  2: { min: 3000, max: 5999, name: "Regional", color: "#3b82f6" },
  3: { min: 6000, max: 9999, name: "National", color: "#22c55e" },
  4: { min: 10000, max: 14999, name: "Major", color: "#a855f7" },
  5: { min: 15000, max: Infinity, name: "Elite", color: "#f59e0b" }
};

export const REPUTATION_POINTS = {
  ON_TIME_ARRIVAL: 35,
  ON_TIME_DEPARTURE: 25,
  PAX_SATISFACTION_80: 15,
  NO_CANCELLATIONS_DAILY: 10,
  CLEAN_SAFETY_DAY: 5,
  DIVERSION: -50,
  CANCELLATION: -75,
  MAJOR_DELAY_90: -40,
  MINOR_DELAY_30: -15,
  SAFETY_INCIDENT: -100,
  CREW_LEGALITY_BUST: -60,
  MAINTENANCE_VIOLATION: -40
};

export function createReputationState() {
  return {
    currentScore: 0,
    currentTier: 1,
    dailyHistory: [],
    todayPoints: 0,
    todayEvents: [],
    eos: {
      hasEOS: false,
      consecutiveDays: 0,
      qualificationStart: null,
      lostAt: null,
      lockoutUntil: null
    },
    stats: {
      totalFlights: 0,
      onTimeArrivals: 0,
      onTimeDepartures: 0,
      cancellations: 0,
      diversions: 0,
      safetyIncidents: 0
    }
  };
}

export function recordReputationEvent(state, eventType, details = {}) {
  const points = REPUTATION_POINTS[eventType];
  if (points === undefined) return state;
  
  const event = {
    type: eventType,
    points,
    timestamp: new Date().toISOString(),
    details
  };
  
  const newStats = { ...state.stats };
  switch (eventType) {
    case 'ON_TIME_ARRIVAL':
      newStats.onTimeArrivals++;
      newStats.totalFlights++;
      break;
    case 'ON_TIME_DEPARTURE':
      newStats.onTimeDepartures++;
      break;
    case 'CANCELLATION':
      newStats.cancellations++;
      break;
    case 'DIVERSION':
      newStats.diversions++;
      newStats.totalFlights++;
      break;
    case 'SAFETY_INCIDENT':
      newStats.safetyIncidents++;
      break;
    case 'MAJOR_DELAY_90':
    case 'MINOR_DELAY_30':
      newStats.totalFlights++;
      break;
  }
  
  return {
    ...state,
    todayPoints: state.todayPoints + points,
    todayEvents: [...state.todayEvents, event],
    stats: newStats
  };
}

export function recordFlightCompletion(state, flight) {
  let newState = { ...state };
  
  if (flight.totalDelayMinutes <= 14) {
    newState = recordReputationEvent(newState, 'ON_TIME_ARRIVAL', { flightId: flight.id, delay: flight.totalDelayMinutes });
  } else if (flight.totalDelayMinutes >= 90) {
    newState = recordReputationEvent(newState, 'MAJOR_DELAY_90', { flightId: flight.id, delay: flight.totalDelayMinutes });
  } else if (flight.totalDelayMinutes >= 30) {
    newState = recordReputationEvent(newState, 'MINOR_DELAY_30', { flightId: flight.id, delay: flight.totalDelayMinutes });
  }
  
  if (flight.passengerSatisfaction >= 80) {
    newState = recordReputationEvent(newState, 'PAX_SATISFACTION_80', { flightId: flight.id });
  }
  
  return newState;
}

function calculateRollingScore(history) {
  if (history.length === 0) return 0;
  const decayRate = 0.033;
  let total = 0;
  
  history.forEach((day, index) => {
    const daysAgo = history.length - 1 - index;
    const decayMultiplier = Math.pow(1 - decayRate, daysAgo);
    total += day.points * decayMultiplier;
  });
  
  return Math.floor(total);
}

export function calculateTier(score) {
  for (let tier = 5; tier >= 1; tier--) {
    if (score >= TIER_THRESHOLDS[tier].min) return tier;
  }
  return 1;
}

export function processEndOfDay(state, gameDate) {
  let finalPoints = state.todayPoints;
  const events = [...state.todayEvents];
  
  const hadCancellation = events.some(e => e.type === 'CANCELLATION');
  if (!hadCancellation && state.stats.totalFlights > 0) {
    finalPoints += REPUTATION_POINTS.NO_CANCELLATIONS_DAILY;
    events.push({ type: 'NO_CANCELLATIONS_DAILY', points: REPUTATION_POINTS.NO_CANCELLATIONS_DAILY, timestamp: new Date().toISOString(), details: { automatic: true } });
  }
  
  const hadSafetyIssue = events.some(e => e.type === 'SAFETY_INCIDENT' || e.type === 'CREW_LEGALITY_BUST');
  if (!hadSafetyIssue && state.stats.totalFlights > 0) {
    finalPoints += REPUTATION_POINTS.CLEAN_SAFETY_DAY;
    events.push({ type: 'CLEAN_SAFETY_DAY', points: REPUTATION_POINTS.CLEAN_SAFETY_DAY, timestamp: new Date().toISOString(), details: { automatic: true } });
  }
  
  const dailyRecord = { date: gameDate, points: finalPoints, events };
  const newHistory = [...state.dailyHistory, dailyRecord].slice(-30);
  const newScore = calculateRollingScore(newHistory);
  const newTier = calculateTier(newScore);
  
  return {
    ...state,
    dailyHistory: newHistory,
    currentScore: newScore,
    currentTier: newTier,
    todayPoints: 0,
    todayEvents: []
  };
}

export function getTierInfo(tier) {
  return TIER_THRESHOLDS[tier] || TIER_THRESHOLDS[1];
}

export function getTierProgress(score) {
  const currentTier = calculateTier(score);
  if (currentTier === 5) return { progress: 1, pointsToNext: 0, nextTier: null };
  
  const currentMin = TIER_THRESHOLDS[currentTier].min;
  const nextMin = TIER_THRESHOLDS[currentTier + 1].min;
  const progress = (score - currentMin) / (nextMin - currentMin);
  
  return {
    progress: Math.min(1, Math.max(0, progress)),
    pointsToNext: nextMin - score,
    nextTier: currentTier + 1
  };
}

export function getOTPPercentage(stats) {
  if (stats.totalFlights === 0) return 100;
  return Math.round((stats.onTimeArrivals / stats.totalFlights) * 100);
}

export default {
  createReputationState,
  recordReputationEvent,
  recordFlightCompletion,
  processEndOfDay,
  getTierInfo,
  getTierProgress,
  getOTPPercentage,
  TIER_THRESHOLDS,
  REPUTATION_POINTS
};